<template>
  <div class="simplified-criterion-card">
    <div class="simplified-criterion-header">
      <div v-if="isViewOnly || hideAction" class="match-all-badge">
        <i class="fa fa-check-double me-2"></i>
        {{ operator === 'OR' ? 'Match Any Condition' : 'Match All Conditions' }}
      </div>
      <div v-else class="match-operator-select">
        <i class="fa fa-check-double me-2"></i>
        <select
          class="form-select form-select-sm"
          :value="operator"
          @change="$emit('update:operator', $event.target.value)"
          aria-label="Match operator"
        >
          <option value="AND">Match ALL conditions</option>
          <option value="OR">Match ANY condition</option>
        </select>
      </div>
    </div>

    <div class="simplified-criterion-body">
      <div
        v-for="(element, index) in criterionValues"
        :key="index"
        class="simplified-criterion-row"
      >
        <div class="criterion-field column-field">
          <label class="criterion-label">
            <i class="fa fa-table-columns me-2"></i>
            Column
          </label>
          <div>
            <Multiselect
              v-model="element.column"
              :options="availableColumnsForIndex(index)"
              placeholder="Select column..."
              :searchable="true"
              :disabled="isViewOnly || hideAction"
              @select="handleColumnSelect($event, index)"
              @clear="handleColumnDeselect(index)"
            />
          </div>
        </div>

        <div class="criterion-field values-field">
          <label class="criterion-label">
            <i class="fa fa-list me-2"></i>
            Values
          </label>
          <div>
            <div v-if="loadingValues[index]" class="text-center py-2">
              <div class="spinner-border spinner-border-sm text-primary"></div>
              <span class="ms-2 text-muted small">Loading values...</span>
            </div>
            <Multiselect
              v-else
              v-model="element.values"
              placeholder="Select or type values..."
              :searchable="true"
              mode="tags"
              :options="valueOptionsFor(element.column)"
              :disabled="isViewOnly || hideAction || !element.column"
              :no-options-text="element.column ? 'No values found' : 'Please select column first'"
              :create-option="true"
              :add-option-on="['enter', ',']"
            />
          </div>
        </div>

        <div v-if="!hideAction" class="delete-criterion-btn-container">
          <button
            class="btn btn-sm btn-outline-danger delete-criterion-btn"
            @click="isViewOnly ? null : handleDeleteCriterion(index)"
            :disabled="criterionValues.length <= 1"
            title="Delete criterion"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="!hideAction" class="simplified-criterion-footer">
      <button
        class="btn btn-sm btn-outline-primary add-criterion-btn"
        @click="isViewOnly ? null : addCriterion()"
        :disabled="isAddDisabled"
        title="Add criterion"
      >
        <i class="fa fa-plus me-2"></i>
        Add Criterion
      </button>
    </div>
  </div>
</template>

<script>
import Multiselect from '@vueform/multiselect'
import { sanitizeCriterionValues } from '../../utils/criterionUtils'

export default {
  name: 'SimplifiedCriterion',
  components: {
    Multiselect,
  },
  props: {
    criteria: {
      type: Array,
      default: () => [{ column: null, values: [] }]
    },
    columns: {
      type: Array,
      default: () => []
    },
    columnValues: {
      type: Object,
      default: () => ({})
    },
    loadingValues: {
      type: Object,
      default: () => ({})
    },
    isViewOnly: {
      type: Boolean,
      default: false
    },
    hideAction: {
      type: Boolean,
      default: false
    },
    operator: {
      type: String,
      default: 'AND'
    },
  },
  data() {
    return {
      criterionValues: [],
    }
  },
  emits: ['update:criteria', 'update:operator', 'columnSelected', 'columnDeselected'],
  mounted() {
    this.criterionValues = JSON.parse(JSON.stringify(this.criteria))
  },
  computed: {
    isAddDisabled() {
      // Disable if last criterion is incomplete or no columns available
      const lastCriterion = this.criterionValues[this.criterionValues.length - 1]
      if (!lastCriterion || !lastCriterion.column || lastCriterion.values.length === 0) {
        return true
      }
      // Also disable if all columns are used
      return this.availableColumnsForIndex(this.criterionValues.length).length === 0
    },
  },
  methods: {
    valueOptionsFor(column) {
      // Multiselect crashes on a null option (isSelected reads option.group), and
      // criteria saved before the builders were fixed can still hold one
      return sanitizeCriterionValues(this.columnValues[column])
    },
    availableColumnsForIndex(currentIndex) {
      // Get columns not already selected in other criteria, but always keep this
      // row's own column: an advanced criterion flattened into simple rows can
      // legitimately repeat a column, and Multiselect resolves its value through
      // the options, so dropping it renders an empty "Select column..."
      const currentColumn = this.criterionValues[currentIndex]?.column
      const selectedColumns = this.criterionValues
        .filter((_, idx) => idx !== currentIndex)
        .map(c => c.column)
        .filter(c => c)
      return this.columns.filter(c => c === currentColumn || !selectedColumns.includes(c))
    },
    handleColumnSelect(value, index) {
      this.criterionValues[index].values = []
      this.$emit('columnSelected', { column: value, index })
      this.emitUpdate()
    },
    handleColumnDeselect(index) {
      const oldColumn = this.criterionValues[index].column
      this.criterionValues[index].column = null
      this.criterionValues[index].values = []
      this.$emit('columnDeselected', { column: oldColumn, index })
      this.emitUpdate()
    },
    handleDeleteCriterion(index) {
      if (this.criterionValues.length > 1) {
        this.criterionValues.splice(index, 1)
        this.emitUpdate()
      }
    },
    addCriterion() {
      this.criterionValues.push({ column: null, values: [] })
      this.emitUpdate()
    },
    emitUpdate() {
      this.$emit('update:criteria', this.criterionValues)
    },
  },
  watch: {
    criteria: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.criterionValues)) {
          this.criterionValues = JSON.parse(JSON.stringify(newVal))
        }
      },
      deep: true
    },
    criterionValues: {
      handler() {
        this.emitUpdate()
      },
      deep: true
    }
  },
}
</script>

<style scoped>
@import '../../../node_modules/@vueform/multiselect/themes/default.css';

.simplified-criterion-card {
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.simplified-criterion-header {
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-bottom: 2px solid #dee2e6;
}

.match-operator-select {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  color: var(--bs-primary, #2C7BE5);
}

.match-operator-select .form-select {
  width: auto;
  font-weight: 600;
  color: var(--bs-primary, #2C7BE5);
  border-color: #d2ddec;
}

.match-all-badge {
  font-weight: 600;
  font-size: 1rem;
  color: var(--bs-primary, #2C7BE5);
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.simplified-criterion-body {
  padding: 1.5rem;
  background-color: #fafbfc;
}

.simplified-criterion-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: white;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
}

.simplified-criterion-row:hover {
  border-color: var(--bs-primary, #2C7BE5);
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.1);
}

.simplified-criterion-row:last-child {
  margin-bottom: 0;
}

.criterion-field {
  flex: 1;
}

.criterion-field.column-field {
  flex: 1;
}

.criterion-field.values-field {
  flex: 2;
}

.criterion-label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: #12263f;
  margin-bottom: 0.5rem;
}

.delete-criterion-btn-container {
  display: flex;
  align-items: flex-end;
  padding-bottom: 0.25rem;
}

.delete-criterion-btn {
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
}

.delete-criterion-btn:hover:not(:disabled) {
  transform: scale(1.1);
  background-color: var(--bs-danger);
  color: white;
  border-color: var(--bs-danger);
}

.simplified-criterion-footer {
  display: flex;
  justify-content: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.add-criterion-btn {
  font-weight: 500;
  padding: 0.5rem 1.5rem;
  transition: all 0.2s ease;
}

.add-criterion-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(13, 110, 253, 0.2);
  background-color: var(--bs-primary);
  color: white;
  border-color: var(--bs-primary);
}

.add-criterion-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Multiselect customizations */
:deep(.multiselect) {
  border: 1px solid #d2ddec;
  border-radius: 0.375rem;
}

:deep(.multiselect.is-disabled) {
  background-color: #edf2f9;
}

:deep(.multiselect-tag) {
  background: #dbeafe;
  color: #2563eb;
}

:deep(.multiselect-tag-remove:hover) {
  background: #2563eb;
  color: white;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .simplified-criterion-row {
    flex-wrap: wrap;
  }

  .criterion-field.column-field,
  .criterion-field.values-field {
    flex: 1 1 100%;
  }

  .delete-criterion-btn-container {
    flex: 1 1 100%;
    justify-content: center;
    align-items: center;
    padding-bottom: 0;
    margin-top: 0.5rem;
  }
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .simplified-criterion-card {
      border-color: $dark-border-color !important;
    }
    .simplified-criterion-header {
      background: linear-gradient(135deg, $dark-elevated-bg, $dark-card-bg) !important;
      border-bottom-color: $dark-border-color !important;
    }
    .simplified-criterion-body {
      background-color: $dark-card-bg !important;
    }
    .simplified-criterion-footer {
      background-color: $dark-card-bg !important;
      border-top-color: $dark-border-color !important;
    }
    .simplified-criterion-row {
      background-color: $dark-elevated-bg !important;
      border-color: $dark-border-color !important;;
    }
    .multiselect-search {
      background-color: $dark-input-bg !important;
    }
}
</style>